import React from 'react';
import { FileText, Receipt, CheckCircle2, Clock, AlertCircle } from 'lucide-react';
import { ResponsiveAmount, AccountCodeLink } from '../../ui/ResponsiveAmount';

interface TaxWidgetProps {
  data: any;
}

/**
 * Comptes SYSCOHADA pour la TVA
 */
const TAX_ACCOUNTS = {
  tvaCollectee: { code: '4431', label: 'TVA Collectée' },
  tvaDeductible: { code: '4451', label: 'TVA Déductible' },
  impots: { code: '44', label: 'État et collectivités publiques' }
};

export function TaxWidget({ data }: TaxWidgetProps) {
  const taxSummary = data?.taxSummary;
  
  if (!taxSummary) {
    return (
      <div className="flex items-center justify-center h-full text-muted-foreground">
        <p className="text-sm">Aucune donnée fiscale disponible</p>
      </div>
    );
  }

  const vatCollected = taxSummary.vatCollected || 0;
  const vatPaid = taxSummary.vatPaid || 0;
  const vatDue = taxSummary.vatDue || (vatCollected - vatPaid);
  const corporateTax = taxSummary.corporateTax || 0;
  const nextDeadline = taxSummary.nextDeadline;
  
  const taxItems = [
    {
      label: 'TVA Collectée',
      value: vatCollected,
      icon: Receipt,
      color: 'text-blue-600 dark:text-blue-400',
      accountCode: TAX_ACCOUNTS.tvaCollectee.code
    },
    {
      label: 'TVA Déductible',
      value: vatPaid,
      icon: FileText,
      color: 'text-green-600 dark:text-green-400',
      accountCode: TAX_ACCOUNTS.tvaDeductible.code
    },
    {
      label: 'TVA à Payer',
      value: vatDue,
      icon: vatDue > 0 ? AlertCircle : CheckCircle2,
      color: vatDue > 0 ? 'text-orange-600 dark:text-orange-400' : 'text-green-600 dark:text-green-400',
      accountCode: TAX_ACCOUNTS.impots.code
    },
    {
      label: 'Impôt sur Sociétés',
      value: corporateTax,
      icon: FileText,
      color: 'text-purple-600 dark:text-purple-400',
      accountCode: '441'
    }
  ];

  // Format deadline
  const formatDeadline = (dateStr: string): string => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffDays = Math.ceil((date.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) return 'Dépassé';
    if (diffDays === 0) return "Aujourd'hui";
    if (diffDays === 1) return 'Demain';
    if (diffDays <= 7) return `Dans ${diffDays} jours`;
    return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
  };

  return (
    <div className="space-y-3">
      {/* Header */}
      <div className="flex items-center justify-between pb-2 border-b border-border">
        <span className="text-sm font-medium text-muted-foreground">Obligations Fiscales</span>
        {nextDeadline && (
          <div className="flex items-center gap-1 text-xs">
            <Clock className="h-3 w-3 text-muted-foreground" />
            <span className="text-muted-foreground">Prochaine échéance:</span>
            <span className="font-medium text-orange-600 dark:text-orange-400">
              {formatDeadline(nextDeadline)}
            </span>
          </div>
        )}
      </div>

      {/* Tax Items Grid */}
      <div className="grid grid-cols-2 gap-3">
        {taxItems.map((item) => {
          const Icon = item.icon;
          return (
            <div key={item.label} className="p-2 rounded-lg bg-muted/50 border border-border/50">
              <div className="flex items-center gap-1.5 mb-1">
                <Icon className={`h-3.5 w-3.5 flex-shrink-0 ${item.color}`} />
                <span className="text-xs text-muted-foreground truncate">{item.label}</span>
                {item.accountCode && (
                  <AccountCodeLink code={item.accountCode} variant="subtle" />
                )}
              </div>
              <ResponsiveAmount 
                value={item.value}
                size="sm"
                className="font-semibold"
                colorize={item.value < 0}
              />
            </div>
          );
        })}
      </div>

      {/* TVA Balance Bar */}
      <div className="pt-2">
        <div className="flex items-center justify-between text-xs mb-1">
          <span className="text-muted-foreground">Balance TVA</span>
          <span className={`font-medium ${vatDue > 0 ? 'text-orange-600' : 'text-green-600'}`}>
            {vatDue > 0 ? 'À payer' : 'Crédit'}
          </span>
        </div>
        <div className="h-2 bg-muted rounded-full overflow-hidden">
          {vatCollected > 0 && (
            <div className="flex h-full">
              <div 
                className="bg-blue-500 transition-all duration-500"
                style={{ width: `${(vatPaid / vatCollected) * 100}%` }}
              />
              <div 
                className="bg-orange-500 transition-all duration-500"
                style={{ width: `${(vatDue / vatCollected) * 100}%` }}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
